import React from 'react';
import {
  View,
  Text,
  Pressable,
  SectionList,
} from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import {
  BodyText,
  HeaderText,
  Touchable,
} from '../../../ui';
import { BasicSearchIcon, MapsPinIcon } from '../../../ui/Icons';
import toHighlightedParts from '../toHighlightedParts';
import './QuerySuggestionsList.module.scss';
import { IQuerySuggestion, IQuerySuggestionList } from '../SearchPopover/types';
import { useInTest } from '../../../../hooks';
import { DP_358_SEARCH_LOCATION_AUTOCOMPLETE_TEST_NAME } from '../SearchPopover/constants';

type QuerySuggestionsListProps = {
  onSelect?: (item: IQuerySuggestion) => void;
  suggestions?: Array<IQuerySuggestionList>;
  renderSuggestion?: (
    item: IQuerySuggestion,
    suggestionText: React.ReactNode
  ) => React.ReactElement;
  inDP358Test?: boolean;
};

/**
 * Given an autocomplete suggestion, return the suggestion text, including anyareas that need to
 * be emphasized, as a react element.
 * @param {Object} suggestion - The search autocomplete suggestion item.
 * @return {ReactComponent[]} The formatted text.
 */
const formatSuggestion = suggestion => {
  const matches = toHighlightedParts(
    suggestion.text,
    suggestion.matchedSubstrings || [],
  );
  return matches.map(({
    text, isMatch, idx,
  }) => (
    <Text key={`${text}-${idx}`}>
      {isMatch ? <Text styleName="matched-text">{text}</Text> : text}
    </Text>
  ));
};

const renderItemSeparator = () => <View styleName="item-separator" />;

const renderSectionSeparator = ({ trailingItem }) => (trailingItem ? (
  <View styleName="item-separator" />
) : (
  <View styleName="section-separator" />
));

class QuerySuggestionsListClass extends React.PureComponent<
QuerySuggestionsListProps
> {
  static defaultProps = {
    suggestions: [],
    onSelect: () => {},
    renderSuggestion: null,
    inDP358Test: false,
  };

  /**
   * Render an individual search suggestion.
   */
  renderSuggestion = ({ item }): React.ReactElement => {
    const { onSelect, renderSuggestion } = this.props;
    const suggestionText: React.ReactNode = formatSuggestion(item);

    if (typeof renderSuggestion === 'function') {
      return renderSuggestion(item, suggestionText);
    }

    let icon = <BasicSearchIcon color={BrandColor.Metal} height={20} width={20} />;
    if (item?.data?.address) {
      icon = <MapsPinIcon color={BrandColor.Metal} height={20} width={20} />;
    }

    if (this.props.inDP358Test === true) {
      return (
        <Pressable onPress={() => onSelect(item)} testID={item.testId}>
          <Inline
            borderColor={BrandColor.WhiteWash}
            borderBottomWidth={1}
            borderStyle="solid"
            paddingY={16}
            justifyContent="flex-start"
            alignItems="center"
            space={8}
            paddingLeft={12}
          >
            {icon}
            <BodyText color={BrandColor.Metal}>{suggestionText}</BodyText>
          </Inline>
        </Pressable>
      );
    }

    return (
      <Touchable onClick={() => onSelect(item)} testID={item.testId}>
        <Inline
          borderColor={BrandColor.WhiteWash}
          borderBottomWidth={1}
          borderStyle="solid"
          paddingY={16}
          justifyContent="flex-start"
          alignItems="center"
        >
          <BodyText color={BrandColor.Metal} weight="medium">{suggestionText}</BodyText>
        </Inline>
      </Touchable>
    );
  };

  /**
     * Render the header of a section of search suggestions.
     * @see https://github.com/moroshko/react-autosuggest#render-section-title-prop
     */
  renderSectionTitle = ({ section }) => {
    // only display a section header if the section is not empty
    if (section.data.length && section.title) {
      return (
        <Box paddingY={4} paddingTop={12} paddingLeft={this.props.inDP358Test ? 12 : undefined}>
          <HeaderText level="3" testID={section.testId}>
            {section.title}
          </HeaderText>
        </Box>
      );
    }

    return null;
  };

  renderListHeaderComponent = () => {
    // display separator above list if there are suggestions
    if (this.props.suggestions.length > 0) {
      return renderItemSeparator();
    }

    return null;
  };

  render() {
    const { suggestions } = this.props;

    return (
      <SectionList
        sections={suggestions}
        keyExtractor={item => item.id}
        renderSectionHeader={this.renderSectionTitle}
        ItemSeparatorComponent={renderItemSeparator}
        SectionSeparatorComponent={renderSectionSeparator}
        ListHeaderComponent={this.renderListHeaderComponent}
        renderItem={this.renderSuggestion}
        stickySectionHeadersEnabled={false}
        keyboardShouldPersistTaps="always"
        showsVerticalScrollIndicator={this.props.inDP358Test ? false : undefined}
      />
    );
  }
}

const QuerySuggestionsList = (props: QuerySuggestionsListProps) => {
  const inDP358Test = useInTest(DP_358_SEARCH_LOCATION_AUTOCOMPLETE_TEST_NAME);

  return <QuerySuggestionsListClass {...props} inDP358Test={inDP358Test} />;
};

export default QuerySuggestionsList;
